import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDown, ArrowLeft, LoaderCircle, MoreHorizontal } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/skeleton'
import { useToast } from '../../components/ui/toast'
import { api } from '../../lib/api'
import { addressLabel, formatDay, serviceWindowLabel } from '../../lib/format'
import type { Message } from '../../types'
import { MessageBubble } from './message-bubble'
import { MessageComposer } from './message-composer'

export function ChatPanel({ conversationId }: { conversationId: string }) {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const scroller = useRef<HTMLDivElement>(null)
  const [atBottom, setAtBottom] = useState(true)
  const conversation = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => api.conversation(conversationId),
  })
  const messages = useInfiniteQuery({
    queryKey: ['messages', conversationId],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api.messages(conversationId, pageParam),
    getNextPageParam: (page) => page.nextCursor,
  })
  const allMessages = useMemo(
    () => [...(messages.data?.pages ?? [])].reverse().flatMap((page) => page.items),
    [messages.data],
  )
  const windowInfo = serviceWindowLabel(conversation.data?.serviceWindowExpiresAt)

  useEffect(() => {
    void api.markRead(conversationId).then(() => {
      void queryClient.invalidateQueries({ queryKey: ['conversations'] })
    })
  }, [conversationId, queryClient])

  useEffect(() => {
    if (atBottom) {
      requestAnimationFrame(() =>
        scroller.current?.scrollTo({ top: scroller.current.scrollHeight }),
      )
    }
  }, [allMessages.length, atBottom])

  useEffect(() => {
    requestAnimationFrame(() => scroller.current?.scrollTo({ top: scroller.current.scrollHeight }))
  }, [conversationId])

  const send = useMutation({
    mutationFn: ({ text, file }: { text: string; file?: File }) =>
      file ? api.sendAttachment(conversationId, file, text) : api.sendText(conversationId, text),
    onSuccess: () => {
      setAtBottom(true)
      void queryClient.invalidateQueries({ queryKey: ['messages', conversationId] })
      void queryClient.invalidateQueries({ queryKey: ['conversations'] })
    },
    onError: (error) =>
      showToast(error instanceof Error ? error.message : 'Invio non riuscito', 'error'),
  })
  const retry = useMutation({
    mutationFn: api.retryMessage,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['messages', conversationId] }),
    onError: (error) =>
      showToast(error instanceof Error ? error.message : 'Ritentativo non riuscito', 'error'),
  })

  const onScroll = () => {
    const element = scroller.current
    if (!element) return
    setAtBottom(element.scrollHeight - element.scrollTop - element.clientHeight < 80)
  }

  const loadOlder = async () => {
    const element = scroller.current
    const previousHeight = element?.scrollHeight ?? 0
    await messages.fetchNextPage()
    requestAnimationFrame(() => {
      if (element) element.scrollTop = element.scrollHeight - previousHeight
    })
  }

  if (conversation.isLoading) {
    return (
      <div className="flex h-full flex-col bg-[var(--background)]">
        <div className="flex h-[76px] items-center gap-3 border-b border-[var(--border)] bg-[var(--surface)] px-4">
          <Skeleton className="size-11 rounded-2xl" />
          <div className="flex flex-col gap-2">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      </div>
    )
  }

  if (!conversation.data) {
    return (
      <div className="grid h-full place-items-center p-8 text-center">
        Conversazione non disponibile.
      </div>
    )
  }

  const initials = conversation.data.contact.displayName
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()

  return (
    <div className="relative flex h-full min-h-0 flex-col bg-[var(--background)]">
      <header className="z-10 flex min-h-[76px] items-center gap-3 border-b border-[var(--border)] bg-[var(--surface)] px-3 sm:px-5">
        <Link
          to="/messages"
          aria-label="Torna alle conversazioni"
          className="grid size-11 place-items-center rounded-xl text-[var(--muted-foreground)] hover:bg-[var(--muted)] lg:hidden"
        >
          <ArrowLeft className="size-5" />
        </Link>
        <div className="grid size-11 shrink-0 place-items-center rounded-2xl bg-[var(--muted)] text-sm font-bold">
          {initials || 'WA'}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-base font-semibold">
            {conversation.data.contact.displayName}
          </h1>
          <p className="truncate text-xs text-[var(--muted-foreground)]">
            {addressLabel(conversation.data)}
          </p>
        </div>
        <Badge
          className={
            windowInfo.open
              ? 'hidden bg-[var(--success-soft)] text-[var(--success)] sm:inline-flex'
              : 'hidden bg-[var(--warning-soft)] text-[var(--warning)] sm:inline-flex'
          }
        >
          {windowInfo.label}
        </Badge>
        <Button variant="ghost" size="icon" aria-label="Dettagli conversazione" disabled>
          <MoreHorizontal className="size-5" />
        </Button>
      </header>

      <div
        ref={scroller}
        onScroll={onScroll}
        className="min-h-0 flex-1 overflow-y-auto px-3 py-5 sm:px-6 lg:px-10"
      >
        <div className="mx-auto flex max-w-4xl flex-col gap-2.5">
          {messages.hasNextPage && (
            <div className="pb-3 text-center">
              <Button
                variant="secondary"
                size="sm"
                disabled={messages.isFetchingNextPage}
                onClick={() => void loadOlder()}
              >
                {messages.isFetchingNextPage ? (
                  <LoaderCircle className="size-4 animate-spin" />
                ) : null}
                {messages.isFetchingNextPage ? 'Caricamento…' : 'Messaggi precedenti'}
              </Button>
            </div>
          )}
          {messages.isLoading &&
            Array.from({ length: 7 }, (_, index) => (
              <Skeleton
                key={index}
                className={`h-16 ${index % 2 ? 'ml-auto w-2/3' : 'w-3/5'} rounded-2xl`}
              />
            ))}
          {messages.isError && (
            <div className="rounded-2xl border border-[var(--destructive-border)] bg-[var(--destructive-soft)] p-5 text-center text-sm text-[var(--destructive)]">
              Impossibile caricare lo storico.{' '}
              <button className="font-bold underline" onClick={() => void messages.refetch()}>
                Riprova
              </button>
            </div>
          )}
          {!messages.isLoading && allMessages.length === 0 && (
            <div className="py-20 text-center text-sm text-[var(--muted-foreground)]">
              Nessun messaggio in questa conversazione.
            </div>
          )}
          {allMessages.map((message: Message, index) => {
            const previous = allMessages[index - 1]
            const showDay =
              !previous || formatDay(previous.occurredAt) !== formatDay(message.occurredAt)
            return (
              <div key={message.id} className="contents">
                {showDay && (
                  <div className="my-3 flex items-center gap-3" role="separator">
                    <div className="h-px flex-1 bg-[var(--border)]" />
                    <span className="text-xs font-semibold text-[var(--muted-foreground)]">
                      {formatDay(message.occurredAt)}
                    </span>
                    <div className="h-px flex-1 bg-[var(--border)]" />
                  </div>
                )}
                <MessageBubble message={message} onRetry={() => retry.mutate(message.id)} />
              </div>
            )
          })}
        </div>
      </div>

      {!atBottom && (
        <Button
          size="icon"
          variant="secondary"
          className="absolute bottom-28 right-5 z-10 rounded-full shadow-lg"
          aria-label="Vai ai messaggi recenti"
          onClick={() => {
            setAtBottom(true)
            scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: 'smooth' })
          }}
        >
          <ArrowDown className="size-5" />
        </Button>
      )}

      <MessageComposer
        enabled={windowInfo.open}
        disabledReason={windowInfo.label}
        sending={send.isPending}
        onSend={(input) => send.mutateAsync(input).then(() => undefined)}
      />
    </div>
  )
}
